<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Vous avez créé votre visite virtuelle — maintenant, comment la faire
            travailler pour vous ? Ajouter votre visite à votre annonce
            immobilière n'est plus une option différenciante : c'est une attente
            de base des acheteurs. Voici comment publier et distribuer votre
            visite pour un impact maximal.
          </p>
        </section>

        <section>
          <h2>Étape 1 — Obtenir le lien de partage depuis Travvir</h2>
          <p>
            Ouvrez votre visite dans Travvir et appuyez sur le bouton
            <strong>Partager</strong>. Vous obtenez deux éléments clés :
          </p>
          <ul>
            <li>
              <strong>Lien direct</strong> — URL courte que vous pouvez
              copier-coller partout
            </li>
            <li>
              <strong>Code iFrame</strong> — bloc HTML pour intégrer la visite
              directement sur une page web
            </li>
          </ul>
          <p>
            Conservez ces deux éléments dans un document texte pour les utiliser
            lors de la mise en ligne de l'annonce.
          </p>
        </section>

        <section>
          <h2>Étape 2 — Ajouter la visite à votre annonce MLS</h2>

          <h3>Localiser le champ Tour Virtuel</h3>
          <p>
            La grande majorité des plateformes MLS et des portails immobiliers
            (SeLoger, Logic-Immo, Bien'ici, Leboncoin Immo) disposent d'un champ
            dédié intitulé « Tour virtuel », « URL de visite », « Lien vidéo »
            ou équivalent. Ce champ accepte une URL directe.
          </p>

          <h3>Coller le lien Travvir</h3>
          <p>
            Collez simplement le lien direct de votre visite dans ce champ. La
            plateforme affichera automatiquement un bouton « Visite virtuelle »
            sur l'annonce, visible par tous les acheteurs.
          </p>

          <h3>Vérifier l'affichage</h3>
          <p>
            Après enregistrement, visualisez l'annonce côté acheteur pour vous
            assurer que le lien fonctionne et que la visite s'ouvre
            correctement.
          </p>
        </section>

        <section>
          <h2>Étape 3 — Distribution multi-canal</h2>

          <h3>Portails et agrégateurs</h3>
          <p>
            Si votre logiciel CRM immobilier est connecté à plusieurs portails,
            le lien de visite se propage automatiquement. Sinon, mettez à jour
            chaque portail manuellement — l'opération prend 5 minutes par
            plateforme.
          </p>

          <h3>Votre site web</h3>
          <p>
            Intégrez le code iFrame directement sur la page de la propriété de
            votre site. Cela améliore votre SEO local (temps passé sur la page)
            et offre une expérience premium à vos visiteurs.
          </p>

          <h3>E-mail aux prospects</h3>
          <p>
            Envoyez le lien direct à votre liste de contacts intéressés par des
            biens similaires. Les e-mails avec lien de visite virtuelle
            affichent des taux d'ouverture 2× supérieurs à la moyenne.
          </p>

          <h3>Réseaux sociaux</h3>
          <p>
            Partagez le lien sur votre page professionnelle Facebook, LinkedIn
            et Instagram. Accompagnez-le d'une capture d'écran de la visite pour
            attirer l'œil dans le fil d'actualité.
          </p>
        </section>

        <section>
          <h2>Bonnes pratiques pour maximiser l'engagement</h2>
          <ul>
            <li>
              Mentionnez explicitement « Visite virtuelle disponible » dans le
              titre de l'annonce
            </li>
            <li>
              Placez le lien en haut de la description, pas seulement à la fin
            </li>
            <li>Créez une vignette d'aperçu attrayante comme première photo</li>
            <li>
              Mettez à jour le lien si vous retournez filmer des pièces
              supplémentaires
            </li>
          </ul>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/visite-virtuelle-immobilier">Comment créer une visite virtuelle immobilière avec votre smartphone en 2026</a></li>
          <li><a href="/blog/fr/integrer-visite-virtuelle-site-web">Comment Intégrer une Visite Virtuelle 360° sur son Site Web 2026</a></li>
          <li><a href="/blog/fr/agent-immobilier-visite-virtuelle-2026">Agent Immobilier et Visite Virtuelle : Pourquoi C'est Indispensable en 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-vs-photos-immobilier">Visite Virtuelle vs Photos Immobilières : Quelle Différence de Performance ?</a></li>
          <li><a href="/blog/fr/visite-virtuelle-immobilier-smartphone">Visite Virtuelle Immobilière avec Smartphone : Guide Complet 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "ajouter-visite-virtuelle-annonce",
);

useBlogSeo({
  title: "Ajouter une Visite Virtuelle à votre Annonce Immobilière | Travvir",
  description:
    "Guide complet pour ajouter et distribuer votre visite virtuelle : MLS, portails immobiliers, site web et réseaux sociaux. Maximisez la visibilité de vos annonces.",
  keywords:
    "ajouter visite virtuelle annonce immobilière, lien visite virtuelle MLS, distribuer visite virtuelle immobilier",
  slug: "ajouter-visite-virtuelle-annonce",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Comment ajouter une visite virtuelle à une annonce MLS ?",
      answer:
        "Obtenez le lien de partage depuis Travvir, puis collez-le dans le champ « Tour Virtuel » ou « URL de visite » du portail MLS.",
    },
    {
      question:
        "Puis-je mettre à jour la visite après publication de l'annonce ?",
      answer:
        "Oui — le lien Travvir reste le même si vous modifiez la visite. Les portails afficheront automatiquement la version mise à jour.",
    },
  ],
});

</script>
